import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Paper
} from '@mui/material';
import { MarkEmailRead, Lock, Send, ArrowBack, Security, PhoneIphone } from '@mui/icons-material';
import NebenanHilfeLogo from '../UI/NebenanHilfeLogo';
import { authAPI } from '../../services/api';

const VerificationForm = ({
  email,
  type = 'registration', // 'registration', 'mfa', 'delete'
  mfaType = 'email', // 'email' or 'totp'
  devCode = '',
  onVerify,
  onResend,
  onCancel
}) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [resending, setResending] = useState(false);
  const [currentDevCode, setCurrentDevCode] = useState(devCode);
  const [currentMfaType, setCurrentMfaType] = useState(mfaType);
  const [fallbackLoading, setFallbackLoading] = useState(false);

  const getTitle = () => {
    switch (type) {
      case 'mfa':
        return currentMfaType === 'totp' 
          ? 'Authenticator-App (TOTP 2FA)' 
          : 'E-Mail Zwei-Faktor-Authentifizierung';
      case 'delete':
        return 'Konto-Löschung bestätigen';
      default:
        return 'E-Mail-Adresse bestätigen';
    }
  };

  const getSubtitle = () => {
    switch (type) {
      case 'mfa':
        return currentMfaType === 'totp'
          ? 'Öffne deine Authenticator-App (z. B. Google oder Microsoft Authenticator) und gib den aktuellen 6-stelligen Code ein.'
          : `Ein 6-stelliger Login-Code wurde an ${email} gesendet.`;
      case 'delete':
        return `Sicherheitscode wurde an ${email} gesendet. Bestätige das endgültige Löschen deines Kontos.`;
      default:
        return `Wir haben einen 6-stelligen Bestätigungscode an ${email} gesendet.`;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code || code.trim().length !== 6) {
      setError('Bitte gib den 6-stelligen Code ein.');
      return;
    }

    setLoading(true);
    setError('');
    setInfo('');

    const result = await onVerify(code.trim(), currentMfaType);
    if (result && !result.success) {
      setError(result.error || 'Ungültiger Code.');
    }
    setLoading(false);
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    setInfo('');
    const result = await onResend(type);
    if (result && result.success) {
      setInfo('Neuer Bestätigungscode wurde per E-Mail gesendet!');
      if (result.devCode) {
        setCurrentDevCode(result.devCode);
      }
    } else {
      setError(result?.error || 'Fehler beim Senden des Codes.');
    }
    setResending(false);
  };

  const handleRequestEmailFallback = async () => {
    setFallbackLoading(true);
    setError('');
    setInfo('');
    try {
      const response = await authAPI.requestTotpFallbackEmail(email);
      if (response.data?.success) {
        setCurrentMfaType('email');
        setInfo('Ein Notfall-Code wurde per E-Mail an dich gesendet.');
        if (response.data.devCode) {
          setCurrentDevCode(response.data.devCode);
        }
      } else {
        setError(response.data?.error || 'Fehler beim Anfordern des E-Mail-Codes.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Verbindung zum Server fehlgeschlagen.');
    } finally {
      setFallbackLoading(false);
    }
  };

  return (
    <Card sx={{ maxWidth: 460, mx: 'auto' }}>
      <CardContent sx={{ p: { xs: 4, md: 5 } }}>
        <Box textAlign="center" mb={3} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <NebenanHilfeLogo size="medium" />
          
          <Box sx={{ my: 2, color: type === 'delete' ? 'error.main' : 'primary.main' }}>
            {type === 'delete' ? (
              <Lock sx={{ fontSize: 48 }} />
            ) : currentMfaType === 'totp' ? (
              <Security sx={{ fontSize: 48 }} />
            ) : (
              <MarkEmailRead sx={{ fontSize: 48 }} />
            )}
          </Box>

          <Typography variant="h5" component="h1" sx={{ fontWeight: 800, color: 'text.primary', mb: 1 }}>
            {getTitle()}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {getSubtitle()}
          </Typography>
        </Box>

        {currentDevCode && (
          <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'warning.light', color: 'warning.dark', borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
              💡 DEMO / BETA TEST CODE:
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 900, letterSpacing: 4 }}>
              {currentDevCode}
            </Typography>
          </Paper>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {info && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {info}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="6-stelliger Code"
            value={code}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '').slice(0, 6);
              setCode(val);
              setError('');
            }}
            placeholder="123456"
            disabled={loading}
            inputProps={{
              maxLength: 6,
              style: { textAlign: 'center', fontSize: '1.8rem', letterSpacing: '8px', fontWeight: 'bold' }
            }}
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            color={type === 'delete' ? 'error' : 'primary'}
            disabled={loading || code.length !== 6}
            sx={{ py: 1.5, fontSize: '1.1rem', fontWeight: 700, mb: 2 }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Code Bestätigen'}
          </Button>

          <Box textAlign="center" mt={2}>
            {type === 'mfa' && currentMfaType === 'totp' ? (
              <Button
                size="small"
                onClick={handleRequestEmailFallback}
                disabled={fallbackLoading}
                startIcon={<Send fontSize="small" />}
                sx={{ mb: 2 }}
              >
                {fallbackLoading ? 'Sende E-Mail...' : 'Kein Zugriff auf App? Code per E-Mail anfordern'}
              </Button>
            ) : (
              <Button
                size="small"
                onClick={handleResend}
                disabled={resending}
                startIcon={<Send fontSize="small" />}
                sx={{ mb: 2 }}
              >
                {resending ? 'Sende...' : 'Code erneut senden'}
              </Button>
            )}

            <Divider sx={{ my: 1 }} />

            {onCancel && (
              <Button
                size="small"
                color="inherit"
                onClick={onCancel}
                startIcon={<ArrowBack fontSize="small" />}
                sx={{ mt: 1 }}
              >
                Zurück zur Anmeldung
              </Button>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default VerificationForm;
